import type {
  ForethoughtLiveChatHandoffComponent,
  ZendeskLiveChatIntegrationConfig,
} from 'api/types';
import type {
  ForethoughtEvent,
  ForethoughtSendTrackingEvent,
  ForethoughtZopimCallbackEvent,
  ForethoughtZopimFailureEvent,
  ForethoughtZopimPendingEvent,
  ForethoughtLiveChatSetStepSentEvent,
} from './embed';
import { debounce } from 'utils/debounce';
import { postMessage } from 'utils/postMessage';
import { FORETHOUGHT_IFRAME_ID, TWO_MINUTES_MS } from './constants';
import type { ZendeskJwtAuthFn } from './zendesk';
import { getScriptAttribute, retryFunction } from './utils';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import type { WidgetInitMethod } from 'reducers/config';

type ZopimErrorCallback = (err: Error) => void;

type ZopimErrorCallbackWithData = (
  err: Error,
  data: Record<string, unknown>,
) => void;

/**
 * https://api.zopim.com/web-sdk/#events
 */
type ZopimEvents =
  | 'account_status'
  | 'connection_update'
  | 'department_update'
  | 'visitor_update'
  | 'agent_update'
  | 'chat'
  | 'history'
  | 'error';

type ZopimAccountStatus = 'online' | 'away' | 'offline';

type ZopimDepartmentStatus = 'online' | 'away' | 'offline';

/**
 * https://api.zopim.com/web-sdk/#chat-2
 */
interface ZopimChatBaseEventData {
  nick: string;
  type:
    | 'chat.msg'
    | 'chat.file'
    | 'chat.queue_position'
    | 'chat.memberjoin'
    | 'chat.memberleave'
    | 'chat.request.rating'
    | 'chat.rating'
    | 'chat.comment'
    | 'typing'
    | 'last_read';
}

export interface ZopimAgentInfo {
  avatar_path: string;
  display_name: string;
  nick: string;
  title: string;
}

interface ZopimQuickReply {
  action: {
    type: 'QUICK_REPLY_ACTION';
    value: string;
  };
  text: string;
}

/**
 * https://api.zopim.com/web-sdk/structured-message.html#quick-replies
 */
interface ZopimQuickReplyStructuredMessage {
  msg: string;
  quick_replies: ZopimQuickReply[];
  type: 'QUICK_REPLIES';
}

interface ZopimButton {
  action: {
    type: 'LINK_ACTION' | 'QUICK_REPLY_ACTION';
    value: string;
  };
  text: string;
}

/**
 * https://api.zopim.com/web-sdk/structured-message.html#button-template
 */
interface ZopimButtonsStructuredMessage {
  buttons: ZopimButton[];
  msg: string;
  type: 'BUTTON_TEMPLATE';
}

/**
 * https://api.zopim.com/web-sdk/#chat-msg-chat-event-type
 */
export interface ZopimChatMsgEventData extends ZopimChatBaseEventData {
  chat_id?: string;
  display_name: string;
  msg: string;
  options: string[];
  structured_msg?:
    | ZopimQuickReplyStructuredMessage
    | ZopimButtonsStructuredMessage
    | never;
  timestamp: number;
  translation?: {
    from$string: string;
    msg$string: string;
    to$string: string;
  };
  type: 'chat.msg';
}

interface ZopimMetadata {
  height: number;
  width: number;
}

interface ZopimAttachment {
  metadata?: ZopimMetadata;
  mime_type: string;
  name: string;
  size: number;
  url: string;
}

/**
 * https://api.zopim.com/web-sdk/#chat-file-chat-event-type
 */
export interface ZopimChatFileEventData extends ZopimChatBaseEventData {
  attachment: ZopimAttachment;
  deleted: boolean;
  display_name: string;
  timestamp: number;
  type: 'chat.file';
}

interface ZopimMemberEventData {
  display_name: string;
  timestamp: number;
}

/**
 * https://api.zopim.com/web-sdk/#chat-memberjoin-chat-event-type
 */
export interface ZopimMemberJoinEventData
  extends ZopimMemberEventData,
    ZopimChatBaseEventData {
  type: 'chat.memberjoin';
}

/**
 * https://api.zopim.com/web-sdk/#chat-memberleave-chat-event-type
 */
export interface ZopimMemberLeaveEventData
  extends ZopimMemberEventData,
    ZopimChatBaseEventData {
  type: 'chat.memberleave';
}

/**
 * https://api.zopim.com/web-sdk/#chat-queue_position-chat-event-type
 */
export interface ZopimQueuePositionEventData extends ZopimChatBaseEventData {
  nick: 'system:queue';
  queue_position: number;
  type: 'chat.queue_position';
}

/**
 * https://api.zopim.com/web-sdk/#typing-event
 */
export interface ZopimTypingEventData extends ZopimChatBaseEventData {
  type: 'typing';
  typing: boolean;
}

/**
 * https://api.zopim.com/web-sdk/#chat-request-rating-chat-event-type
 */
export interface ZopimChatRequestRatingEventData
  extends ZopimChatBaseEventData {
  type: 'chat.request.rating';
}

type ZopimChatEventData =
  | ZopimChatMsgEventData
  | ZopimChatFileEventData
  | ZopimMemberJoinEventData
  | ZopimMemberLeaveEventData
  | ZopimQueuePositionEventData
  | ZopimTypingEventData
  | ZopimChatRequestRatingEventData;

/**
 * https://api.zopim.com/web-sdk/#39-connection_update-39-event
 */
export type ZopimConnectionStatus = 'connecting' | 'connected' | 'closed';

export interface ZopimError extends Error {
  context: string;
}

export type ZopimEventData<T extends ZopimEvents> = T extends 'agent_update'
  ? ZopimAgentInfo
  : T extends 'chat'
    ? ZopimChatEventData
    : T extends 'connection_update'
      ? ZopimConnectionStatus
      : T extends 'error'
        ? ZopimError
        : unknown;

interface ZopimDepartment {
  id: number;
  name: string;
  status: ZopimDepartmentStatus;
}

interface ZopimInitOptions {
  account_key: string;
  authentication?: { jwt_fn: ZendeskJwtAuthFn };
  suppress_console_error?: boolean;
}

/**
 * https://api.zopim.com/web-sdk/
 */
export interface ZopimSdk {
  /**
   * https://api.zopim.com/web-sdk/#zchat-addtags-tags-callback
   */
  addTags: (tags: string[], callback?: ZopimErrorCallback) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-clearvisitordefaultdepartment-callback
   */
  clearVisitorDefaultDepartment: (callback?: ZopimErrorCallback) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-endchat-options-callback
   */
  endChat: (
    options?: { clear_dept_id_on_chat_ended: boolean },
    callback?: ZopimErrorCallback,
  ) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-getaccountstatus
   */
  getAccountStatus: () => ZopimAccountStatus;
  /**
   * https://api.zopim.com/web-sdk/#zchat-getalldepartments
   */
  getAllDepartments: () => ZopimDepartment[];
  /**
   * https://api.zopim.com/web-sdk/#zchat-getchatlog
   */
  getChatLog: () => ZopimChatEventData[];
  /**
   * https://api.zopim.com/web-sdk/#zchat-getconnectionstatus
   */
  getConnectionStatus: () => ZopimConnectionStatus;
  /**
   * https://api.zopim.com/web-sdk/#zchat-getdepartment-id
   */
  getDepartment: (id: number) => ZopimDepartment | undefined;
  /**
   * https://api.zopim.com/web-sdk/#zchat-getservingagentsinfo
   */
  getServingAgentsInfo: () => ZopimAgentInfo[];
  /**
   * https://api.zopim.com/web-sdk/#zchat-init-options
   */
  init: (options: ZopimInitOptions) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-ischatting
   */
  isChatting: () => boolean;
  /**
   * https://api.zopim.com/web-sdk/#zchat-markasread
   */
  markAsRead: () => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-on-event_name-handler
   */
  on: <T extends ZopimEvents>(
    event: T,
    handler: (event_data: ZopimEventData<T>) => void,
  ) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-reconnect
   */
  reconnect: () => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-removetags-tags-callback
   */
  removeTags: (tags: string[], callback?: ZopimErrorCallback) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-sendchatcomment-comment-callback
   */
  sendChatComment: (comment: string, callback?: ZopimErrorCallback) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-sendchatmsg-msg-callback
   */
  sendChatMsg: (message: string, callback?: ZopimErrorCallback) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-sendchatrating-rating-callback
   */
  sendChatRating: (
    rating: 'good' | 'bad' | 'null',
    callback?: ZopimErrorCallback,
  ) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-sendchatmsg-msg-callback
   *
   * I have added content because we need to send the file content from inside
   * the iframe to the parent page
   */
  sendFile: (
    file: File,
    content?: string | ArrayBuffer | null,
    callback?: ZopimErrorCallbackWithData,
  ) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-sendtyping-is_typing
   */
  sendTyping: (is_typing: boolean) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-setvisitordefaultdepartment-id-callback
   */
  setVisitorDefaultDepartment: (
    id: number,
    callback?: ZopimErrorCallback,
  ) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-setvisitorinfo-options-callback
   */
  setVisitorInfo: (
    visitorInfo: {
      display_name?: string;
      email?: string;
      phone?: string;
    },
    callback?: ZopimErrorCallback,
  ) => void;
  /**
   * https://api.zopim.com/web-sdk/#zchat-un-event_name-handler
   */
  un: <T extends ZopimEvents>(
    event: T,
    handler: (event_data: ZopimEventData<T>) => void,
  ) => void;
}

export type ZopimFunctionWithCallback =
  | 'addTags'
  | 'clearVisitorDefaultDepartment'
  | 'endChat'
  | 'removeTags'
  | 'sendChatComment'
  | 'sendChatMsg'
  | 'sendChatRating'
  | 'sendFile'
  | 'setVisitorDefaultDepartment'
  | 'setVisitorInfo'
  | 'sendTyping'
  | 'reconnect';

type ZopimFunctionArgsHelper<K extends keyof ZopimSdk> = ZopimSdk[K] extends (
  ...args: infer P
) => unknown
  ? P
  : never;

export const ZOPIM_SCRIPT_ID = 'forethought-zopim';

/**
 * Call similar to applyZopimFunction but this will add
 * a callback to the method call and will return a promise
 */
const applyZopimFunctionWithCallback = <K extends ZopimFunctionWithCallback>(
  fn: K,
  ...args: ZopimFunctionArgsHelper<K>
) => {
  return new Promise<void>((resolve, reject) => {
    if (!window.zChat) {
      const zChatError = new Error('window.zChat is not defined');
      postZopimMessage({
        args: args,
        error: zChatError,
        event: 'forethoughtZopimCallback',
        function: fn,
        response: undefined,
      } satisfies ForethoughtZopimCallbackEvent['data']);
      return reject(zChatError);
    }

    // @ts-expect-error TS2556
    window.zChat[fn](...args, (err, res) => {
      // don't log endChat errors because we attempt to
      // end the chat every time we start a new one
      if (err && fn !== 'endChat') {
        sendErrorToSentry({
          consoleError: true,
          error: `Zopim One Chat error ocurred while calling ${fn} -- ${err}`,
        });
      }
      // post callback response back to useHandleZendeskChatInitialization
      postZopimMessage({
        args: args,
        error: err,
        event: 'forethoughtZopimCallback',
        function: fn,
        response: res,
      } satisfies ForethoughtZopimCallbackEvent['data']);
      resolve();
    });
  });
};

/**
 * Debounce due to multiple re-renders / message events
 */
export const debouncedSetupZopimSdk = debounce(
  ({
    accountKey,
    postInit,
    setupListeners,
  }: {
    accountKey: string;
    postInit: () => void;
    setupListeners: () => void;
  }) => {
    setupListeners();
    /**
     * if not connected already, initialize zopim sdk
     */
    if (window.zChat && window.zChat.getConnectionStatus() !== 'connected') {
      const initOptions: ZopimInitOptions = {
        account_key: accountKey,
        suppress_console_error: false,
      };

      // pass along authenticate from parent page
      if (window.zESettings?.webWidget?.authenticate?.chat?.jwtFn) {
        initOptions.authentication = {
          jwt_fn: window.zESettings.webWidget.authenticate.chat.jwtFn,
        };
      }

      window.zChat.init(initOptions);
    }
    postInit();
  },
  500,
);

/**
 * Needed to prevent potential duplicate calls.
 */
let timeout: NodeJS.Timeout;
let interval: NodeJS.Timeout;

const TARGET_ORIGIN = '*';
export const ZOPIM_WEB_SDK_URL =
  'https://dev.zopim.com/web-sdk/1.11.2/web-sdk.js';

const postZopimMessage = (data: Record<string, unknown>) => {
  const ifr = document.getElementById(
    FORETHOUGHT_IFRAME_ID,
  ) as HTMLIFrameElement;
  if (ifr) {
    ifr.contentWindow?.postMessage(data, TARGET_ORIGIN);
  } else {
    window.postMessage(data);
  }
};

const isZopimChatting = (
  liveChatWidgetComponent: ForethoughtLiveChatHandoffComponent,
) => {
  return (
    !!window.zChat &&
    window.zChat.isChatting() &&
    window.zChat
      .getChatLog()
      .some(
        chat =>
          chat.type === 'chat.msg' &&
          chat.msg ===
            liveChatWidgetComponent.component_fields.customer_facing_transcript,
      )
  );
};

/**
 * setTimeout is being used because these zopim APIs are
 * asynchronous but they do not return promises so we have
 * to arbitrarily wait for them to finish
 */
export const initZopimChat = async ({
  conversationId,
  conversationIdUsedInPreviousSession,
  integrationFields,
  liveChatWidgetComponent,
}: {
  conversationId: string;
  conversationIdUsedInPreviousSession: string;
  integrationFields: ZendeskLiveChatIntegrationConfig['integration_fields'];
  liveChatWidgetComponent: ForethoughtLiveChatHandoffComponent;
}) => {
  if (!liveChatWidgetComponent.isSent) {
    const departmentId = (() => {
      const deptId = integrationFields.department_with_id?.id;
      const deptName = integrationFields.department_with_id?.name;

      if (deptId && !isNaN(parseInt(deptId))) {
        return parseInt(deptId);
      }

      // this covers a weird testing scenario where I am using
      // a context variable as the department input on begin zendesk chat action
      if (deptName && !isNaN(parseInt(deptName))) {
        return parseInt(deptName);
      }
    })();
    const tags = [
      ...(integrationFields.chat_tags || []),
      `ft-conversation-id_${conversationId}`,
    ];
    const shouldDoAvailabilityCheck = () => {
      if (
        liveChatWidgetComponent.component_fields.integration_config
          .integration === 'zendesk' &&
        liveChatWidgetComponent.component_fields.integration_config
          .integration_fields?.ignore_agent_availability
      ) {
        return false;
      }

      return true;
    };

    if (timeout) {
      clearTimeout(timeout);
    }

    /**
     * setTimeout here gives enough time for debouncedSetupZopimSdk to run
     *
     * `retryFunction` will help deal with the case where zChat is not connected yet.
     * Doing this for now because actually listening to the connected event caused unrecreatable bugs.
     *
     * current logic gives 11 seconds for the sdk / department to come online.
     */
    timeout = setTimeout(async () => {
      try {
        await retryFunction({
          callback: async () => {
            if (window.zChat?.getConnectionStatus() !== 'connected') {
              throw new Error(
                `Zopim SDK not connected -- connection status is ${window.zChat?.getConnectionStatus()}`,
              );
            }

            /**
             * if zopim is already chatting, and the chat contains our transcript,
             * send error to sentry and return so existing chat continues.
             */
            if (isZopimChatting(liveChatWidgetComponent)) {
              sendErrorToSentry({
                consoleError: true,
                conversationId: conversationId,
                error: new Error('Zopim chat already in progress'),
              });
              return;
            }

            /**
             * availability check
             *
             * if getAllDepartments returns an empty array -> throw error and keep trying. The
             * belief here is there is a race condition where the departments are not loaded yet.
             *
             * if getAllDepartments returns an array but department is not found -> fail one chat
             *
             * if department is offline -> fail one chat
             */
            if (departmentId && shouldDoAvailabilityCheck()) {
              if (!window.zChat.getAllDepartments().length) {
                throw new Error('Cannot fetch departments.');
              }

              const department = window.zChat.getDepartment(departmentId);

              if (!department) {
                const departmentNotFoundEvent: ForethoughtZopimFailureEvent['data'] =
                  {
                    error: `Department ${departmentId} not found`,
                    event: 'forethoughtZopimFailure',
                  };
                postZopimMessage(departmentNotFoundEvent);
                return;
              }

              if (department.status === 'offline') {
                const failureEvent: ForethoughtZopimFailureEvent['data'] = {
                  error: `Department ${departmentId} is offline`,
                  event: 'forethoughtZopimFailure',
                };
                postZopimMessage(failureEvent);
                return;
              }
            }
            // set liveChatStatus to 'pending'
            const pendingEvent: ForethoughtZopimPendingEvent['data'] = {
              event: 'forethoughtZopimPending',
            };
            postZopimMessage(pendingEvent);
            // end existing chat
            await applyZopimFunctionWithCallback('endChat', {
              clear_dept_id_on_chat_ended: true,
            });
            // handle department
            if (departmentId) {
              // clear existing visitor default department
              await applyZopimFunctionWithCallback(
                'clearVisitorDefaultDepartment',
              );
              // set visitor default department
              await applyZopimFunctionWithCallback(
                'setVisitorDefaultDepartment',
                departmentId,
              );
            }
            // remove previous conversation tag
            await applyZopimFunctionWithCallback('removeTags', [
              `ft-conversation-id_${conversationIdUsedInPreviousSession}`,
            ]);
            // add tags
            await applyZopimFunctionWithCallback('addTags', tags);
            // set visitor info
            await applyZopimFunctionWithCallback('setVisitorInfo', {
              display_name: integrationFields.visitor_name || '',
              email: integrationFields.visitor_email || '',
            });
            // send message
            await applyZopimFunctionWithCallback(
              'sendChatMsg',
              liveChatWidgetComponent.component_fields
                .customer_facing_transcript,
            );
            // tracking event
            const trackingEventData: ForethoughtSendTrackingEvent['data'] = {
              event: 'forethoughtSendTrackingEvent',
              trackingEvent: {
                conversation_id: conversationId,
                department_id: departmentId,
                display_name: integrationFields.visitor_name,
                email: integrationFields.visitor_email,
                event_type: 'widget-customer-handoff',
                integration: 'zendesk-zopim-live-chat',
                success: true,
                tags: tags,
              },
            };
            postZopimMessage(trackingEventData);
            // set step as sent
            const setStepSentEvent: ForethoughtLiveChatSetStepSentEvent['data'] =
              {
                event: 'forethoughtLiveChatSetStepSent',
              };
            postZopimMessage(setStepSentEvent);
          },
          initialDelayMs: 0,
          intervalMs: 500,
          numRetries: 20,
        });
      } catch (error) {
        const errorString = `${error instanceof Error ? error.message : error}`;
        const failureEvent: ForethoughtZopimFailureEvent['data'] = {
          error: errorString,
          event: 'forethoughtZopimFailure',
        };
        postZopimMessage(failureEvent);
        return;
      }
    }, 1000);
  }
};

/**
 * Used to initialize zopim web sdk on the parent page in order
 * for zopim persistence to work when we are in an iframe.
 */
export const setUpZopimOneChatListener = () => {
  const ifr = document.getElementById(
    FORETHOUGHT_IFRAME_ID,
  ) as HTMLIFrameElement;

  // handlers
  const agentUpdateHandler = (data: ZopimEventData<'agent_update'>) => {
    ifr.contentWindow?.postMessage(
      {
        event: 'forethoughtZopimAgentUpdate',
        zopimData: data,
      },
      TARGET_ORIGIN,
    );
  };
  const chatHandler = (data: ZopimEventData<'chat'>) => {
    ifr.contentWindow?.postMessage(
      {
        event: 'forethoughtZopimChat',
        zopimAgents: window.zChat?.getServingAgentsInfo() || [],
        zopimData: data,
      },
      TARGET_ORIGIN,
    );

    /**
     * Needs to be in a setTimeout or else isChatting will still be true
     */
    setTimeout(() => {
      if (zopimDidAgentEndChat(data)) {
        ifr.contentWindow?.postMessage(
          {
            event: 'forethoughtZopimChatEnded',
            zopimData: data,
          },
          TARGET_ORIGIN,
        );
      }
    });
  };
  const connectionUpdateHandler = (
    data: ZopimEventData<'connection_update'>,
  ) => {
    ifr.contentWindow?.postMessage(
      {
        event: 'forethoughtZopimConnectionUpdate',
        zopimData: data,
      },
      TARGET_ORIGIN,
    );
  };
  const errorHandler = (data: ZopimEventData<'error'>) => {
    ifr.contentWindow?.postMessage(
      {
        error: data,
        event: 'forethoughtZopimError',
      },
      TARGET_ORIGIN,
    );
  };

  // listeners
  const setupListeners = () => {
    window.zChat?.on('agent_update', agentUpdateHandler);
    window.zChat?.on('chat', chatHandler);
    window.zChat?.on('connection_update', connectionUpdateHandler);
    window.zChat?.on('error', errorHandler);

    const chatLog = window.zChat?.getChatLog() || [];
    chatLog.forEach(chat => {
      chatHandler(chat);
    });

    interval = setInterval(() => {
      window.zChat?.markAsRead();
    }, TWO_MINUTES_MS);
  };
  const removeListeners = () => {
    window.zChat?.un('agent_update', agentUpdateHandler);
    window.zChat?.un('chat', chatHandler);
    window.zChat?.un('connection_update', connectionUpdateHandler);
    window.zChat?.un('error', errorHandler);
    if (interval) {
      clearInterval(interval);
    }
  };

  // message listener to listen from iframe
  window.addEventListener('message', (e: ForethoughtEvent) => {
    switch (e.data.event) {
      case 'forethoughtZopimInit':
        const {
          conversationId,
          conversationIdUsedInPreviousSession,
          forethoughtHosted,
          integrationFields,
          liveChatWidgetComponent,
        } = e.data;
        if (window.zChat) {
          debouncedSetupZopimSdk({
            accountKey: integrationFields.account_key,
            postInit: () =>
              initZopimChat({
                conversationId,
                conversationIdUsedInPreviousSession,
                integrationFields,
                liveChatWidgetComponent,
              }),
            setupListeners: setupListeners,
          });
        } else {
          const script = document.createElement('script');
          script.id = ZOPIM_SCRIPT_ID;
          script.src = getScriptUrl(forethoughtHosted);
          script.fetchPriority = 'high';
          script.onload = () => {
            debouncedSetupZopimSdk({
              accountKey: integrationFields.account_key,
              postInit: () =>
                initZopimChat({
                  conversationId,
                  conversationIdUsedInPreviousSession,
                  integrationFields,
                  liveChatWidgetComponent,
                }),
              setupListeners: setupListeners,
            });
          };

          const nonce = getScriptAttribute('nonce');
          if (nonce) {
            script.nonce = nonce;
          }

          document.body.appendChild(script);
        }
        break;
      case 'forethoughtZopimRemove':
        removeListeners();
        document.getElementById(ZOPIM_SCRIPT_ID)?.remove();
        break;
      case 'forethoughtZopimApplyFunction':
        if (window.zChat) {
          // reconstruct file on parent page
          if (e.data.functionName === 'sendFile') {
            try {
              const file: File = e.data.args[0];
              // get file content, we are reading it as a DataURL
              const fileContent: string = e.data.args[1].split(',')[1];
              e.data.args[0] = recreateFile(file, fileContent);
              // remove file content from args before sending to zopim
              applyZopimFunctionWithCallback(
                e.data.functionName,
                ...[e.data.args[0], ...e.data.args.slice(2)],
              );
            } catch (e) {
              // do nothing
              break;
            }
          } else {
            // @ts-expect-error TS2345
            applyZopimFunctionWithCallback(e.data.functionName, ...e.data.args);
          }
        } else {
          const zChatError = new Error('window.zChat is not defined');
          postZopimMessage({
            args: e.data.args,
            error: zChatError,
            event: 'forethoughtZopimCallback',
            function: e.data.functionName,
            response: undefined,
          } satisfies ForethoughtZopimCallbackEvent['data']);
        }
        break;
      case 'forethoughtZopimClearSession':
        if (window.zChat) {
          window.zChat = undefined;
        }
        break;
      case 'forethoughtCheckZChatIsChatting':
        // Handle request from iframe to check zChat.isChatting() status
        const messageId = e.data.messageId;
        let isChatting: boolean | undefined = undefined;

        try {
          if (window.zChat && typeof window.zChat.isChatting === 'function') {
            isChatting = window.zChat.isChatting();
          }
        } catch (error) {
          isChatting = undefined;
        }

        // Send response back to iframe using postZopimMessage helper
        postZopimMessage({
          event: 'forethoughtZChatIsChatting',
          isChatting: isChatting,
          messageId: messageId,
        });
        break;
    }
  });
};

/**
 * Call a function on window.zChat or postMessage so parent page
 * handles the function call depending on our init method.
 *
 * Note - passing in a callback will not work because `applyZopimFunctionWithCallback`
 * overrides it.
 *
 * Example - `applyZopimFunction('script', 'sendChatMsg', 'hello');`
 */
export const applyZopimFunction = <K extends ZopimFunctionWithCallback>(
  widgetInitMethod: WidgetInitMethod,
  fn: K,
  ...args: ZopimFunctionArgsHelper<K>
) => {
  if (widgetInitMethod === 'url') {
    applyZopimFunctionWithCallback(fn, ...args);
  } else {
    // handle sending file from iframe to parent page
    if (fn === 'sendFile') {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result;
        postMessage({
          args: [...args, content],
          event: 'forethoughtZopimApplyFunction',
          functionName: fn,
        });
      };
      reader.readAsDataURL(args[0] as File);
    } else {
      postMessage({
        args: [...args],
        event: 'forethoughtZopimApplyFunction',
        functionName: fn,
      });
    }
  }
};

/**
 * Recreate a file at the parent page from iframe postMessage
 */
const recreateFile = (file: File, content: string): File => {
  const byteCharacters = atob(content);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: file.type });
  return new File([blob], file.name, { type: file.type });
};

/**
 * Agent left and isChatting is false, so we can assume the agent ended the chat
 * and we can end the chat on our side. This is a workaround for the fact that
 * Zopim does not actually tell us when the chat ends.
 *
 * Agent transfer -> we get chat.memberLeave event but isChatting is still true
 *
 * user_not_alive -> we do not get this event, and the next time we call getChatLog
 * there is no history so we stay in the state allowing the user to send messages
 * which will create a new chat.
 *
 */
export const zopimDidAgentEndChat = (data: ZopimEventData<'chat'>): boolean => {
  if (data.type === 'chat.memberleave' && data.nick.startsWith('agent:')) {
    if (window.zChat?.isChatting() === false) {
      return true;
    }
  }

  return false;
};

/**
 * Get the zopim script url
 */
const getScriptUrl = (forethoughtHosted: boolean) => {
  if (forethoughtHosted) {
    try {
      const ifr = document.getElementById(
        FORETHOUGHT_IFRAME_ID,
      ) as HTMLIFrameElement;
      if (ifr && ifr.src) {
        const url = new URL(ifr.src);
        return `${url.origin}/vendor/zendesk/zopim-web-sdk.js`;
      } else {
        return ZOPIM_WEB_SDK_URL;
      }
    } catch {
      return ZOPIM_WEB_SDK_URL;
    }
  }

  return ZOPIM_WEB_SDK_URL;
};
